import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface Identifiable {
  id: string;

  [key: string]: any;
}

export interface OverflowListProps<T extends Identifiable> {
  items: T[];
  itemRenderer: (item: T, index: number) => ReactNode;
  overflowRenderer?: (overflowCount: number) => ReactNode;
}

function OverflowListInner<T extends Identifiable>({ items, itemRenderer, overflowRenderer }: OverflowListProps<T>) {
  const [visibleCount, setVisibleCount] = useState(items.length);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef(new Map<string, HTMLDivElement>());
  const widthsRef = useRef(new Map<string, number>());
  const BADGE_WIDTH = 60;

  const reCalc = useCallback(
    (containerWidth: number) => {
      if (containerWidth <= 0) {
        setVisibleCount(0);
        return;
      }

      const all = items;
      const widthMap = widthsRef.current;
      let used = 0;
      let fit = 0;

      for (const item of all) {
        const w = widthMap.get(item.id) || 0;
        if (used + w <= containerWidth) {
          used += w;
          fit++;
        } else {
          break;
        }
      }

      if (fit === all.length) {
        setVisibleCount(all.length);
        return;
      }

      while (fit > 0 && used + BADGE_WIDTH > containerWidth) {
        fit--;
        const removed = all[fit];
        used -= widthMap.get(removed.id) || 0;
      }
      setVisibleCount(fit);
    },
    [items]
  );

  useEffect(() => {
    const newMap = new Map<string, number>();
    const prev = widthsRef.current;
    for (const item of items) {
      if (prev.has(item.id)) {
        newMap.set(item.id, prev.get(item.id)!);
      } else {
        const el = itemRefs.current.get(item.id);
        if (el) newMap.set(item.id, el.offsetWidth);
      }
    }
    widthsRef.current = newMap;

    const containerWidth = containerRef.current?.clientWidth ?? 0;
    reCalc(containerWidth);
  }, [items, reCalc]);

  const visibleItems = useMemo(() => items.slice(0, visibleCount), [items, visibleCount]);
  const hiddenCount = items.length - visibleCount;
  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexWrap: 'nowrap',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        alignItems: 'center',
        gap: '4px',
        width: '100%',
      }}
    >
      {visibleItems.map((item, idx) => (
        <div key={item.id}>{itemRenderer(item, idx)}</div>
      ))}

      {hiddenCount > 0 && <div>{overflowRenderer ? overflowRenderer(hiddenCount) : <span>+{hiddenCount}</span>}</div>}

      {/* hidden “measuring” row */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          visibility: 'hidden',
          height: 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {items.map((item, idx) => (
          <div
            key={item.id}
            ref={(el) => {
              if (el) itemRefs.current.set(item.id, el);
            }}
            style={{ display: 'inline-block' }}
          >
            {itemRenderer(item, idx)}
          </div>
        ))}
      </div>
    </div>
  );
}

export const OverflowList = React.memo(OverflowListInner) as typeof OverflowListInner;
